import { Formik, type FormikHelpers } from "formik";
import { useId } from "react";
import * as yup from "yup";

import type { CreatePatient } from "@elation/api-client/src/stitch-web";
import {
  CreatePatientSex,
  type CreatePatientSex as CreatePatientSexValue,
} from "@elation/api-client/src/stitch-web/models/createPatientSex";

import { FormikDateInput } from "@src/shared/components/forms/FormikDateInput";
import { FormikInput } from "@src/shared/components/forms/FormikInput";
import { FormikSelect } from "@src/shared/components/forms/FormikSelect";
import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/shared/components/ui/dialog";
import { Spinner } from "@src/shared/components/ui/spinner";
import { NaiveDate } from "@src/shared/utils/types";

export type CreatePatientSubmitValues = Required<
  Pick<CreatePatient, "firstName" | "lastName" | "dob" | "sex">
>;

interface CreatePatientFormValues {
  firstName: string;
  lastName: string;
  dob: NaiveDate | null;
  sex: CreatePatientSexValue | "";
}

export interface CreatePatientDialogProps {
  open: boolean;
  isSubmitting?: boolean;
  error?: string | null;
  showOverlay?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreatePatientSubmitValues) => void | Promise<void>;
}

const validationSchema = yup.object<CreatePatientFormValues>({
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  dob: (yup.mixed() as yup.Schema<NaiveDate | null>)
    .nullable(true)
    .required("Date of birth is required"),
  sex: yup
    .string()
    .oneOf(Object.values(CreatePatientSex), "Sex at birth is required")
    .required("Sex at birth is required") as yup.Schema<
    CreatePatientSexValue | ""
  >,
});

const sexOptions = [
  { label: "Female", value: CreatePatientSex.Female },
  { label: "Male", value: CreatePatientSex.Male },
  { label: "Other", value: CreatePatientSex.Other },
  { label: "Unknown", value: CreatePatientSex.Unknown },
];

function isCreatePatientSex(
  value: CreatePatientFormValues["sex"],
): value is CreatePatientSexValue {
  return Object.values(CreatePatientSex).includes(
    value as CreatePatientSexValue,
  );
}

export const CreatePatientDialog = ({
  open,
  isSubmitting = false,
  error = null,
  showOverlay = true,
  onOpenChange,
  onSubmit,
}: CreatePatientDialogProps) => {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const errorId = `${generatedId}-error`;

  const handleSubmit = async (
    values: CreatePatientFormValues,
    helpers: FormikHelpers<CreatePatientFormValues>,
  ) => {
    if (!values.dob || !isCreatePatientSex(values.sex)) {
      helpers.setSubmitting(false);
      return;
    }
    await onSubmit({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dob: NaiveDate.toString(values.dob),
      sex: values.sex,
    });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isSubmitting && !nextOpen) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        showOverlay={showOverlay}
      >
        <Formik<CreatePatientFormValues>
          initialValues={{ firstName: "", lastName: "", dob: null, sex: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange
        >
          {({ handleSubmit, isSubmitting: isFormikSubmitting }) => {
            const submitting = isSubmitting || isFormikSubmitting;
            return (
              <form onSubmit={handleSubmit} className="grid gap-4" noValidate>
                <DialogHeader>
                  <DialogTitle id={titleId}>Create patient</DialogTitle>
                  <DialogDescription id={descriptionId}>
                    Enter the patient's details to create their record.
                  </DialogDescription>
                </DialogHeader>

                {error ? (
                  <Alert
                    variant="destructive"
                    aria-live="polite"
                    aria-atomic="true"
                    id={errorId}
                  >
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}

                <div className="grid gap-4">
                  <FormikInput
                    name="firstName"
                    label="First name"
                    autoComplete="given-name"
                    required
                    disabled={submitting}
                  />
                  <FormikInput
                    name="lastName"
                    label="Last name"
                    autoComplete="family-name"
                    required
                    disabled={submitting}
                  />
                  <FormikDateInput
                    name="dob"
                    label="Date of birth"
                    required
                    disabled={submitting}
                  />
                  <FormikSelect
                    name="sex"
                    label="Sex at birth"
                    items={sexOptions}
                    placeholder="Select sex at birth"
                    required
                    disabled={submitting}
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? <Spinner className="mr-2" /> : null}
                    Create patient
                  </Button>
                </DialogFooter>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
